import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { authApi, qk } from '@/api';
import { Button } from '@/components/ui/button';

/**
 * `/support-handoff?code=…` — where a Fuorix support admin's "Open read-only"
 * lands (doc 13 "Support panel"). Posts the one-time code, which sets the
 * 30-minute read-only cookie, then goes to the dashboard. The code lives 60 s
 * and burns on first use, so a reload of this page shows the expired message.
 */
export function SupportHandoffPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<'working' | 'expired'>(code ? 'working' : 'expired');
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !code) return;
    started.current = true;
    void authApi
      .supportHandoff(code)
      .then(async () => {
        await qc.invalidateQueries({ queryKey: qk.me() });
        navigate('/', { replace: true });
      })
      .catch(() => setState('expired'));
  }, [code, navigate, qc]);

  return (
    <div className="auth-wrap">
      <div className="w-[380px] max-w-full rounded-2xl border border-line2 bg-surface px-7 pt-7 pb-6 text-center shadow-lg2">
        <div className="flex items-center justify-center gap-2.5 font-cond text-[19px] font-bold">
          <span className="relative size-[26px] rounded-[7px] bg-orange">
            <span className="absolute right-1.5 bottom-1.5 left-1.5 h-[5px] rounded-sm bg-ink" />
          </span>
          SiteDay
        </div>
        {state === 'working' ? (
          <p className="mt-4 text-ink2" role="status">
            Opening the company in read-only support view…
          </p>
        ) : (
          <>
            <h1 className="mt-4 font-cond text-[20px] font-extrabold" role="alert">
              This support link has expired
            </h1>
            <p className="mt-2 text-xs text-ink3">
              Support links work once and for one minute. Go back to the support panel and open the
              company again.
            </p>
            <Link to="/sign-in" className="mt-4 inline-block">
              <Button>Go to sign-in</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
